import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { currentUserIsAdminSelector } from '../../../../redux/session/currentUser/selectors'
import { useCtxDimensions } from '../../../../features/dimension/queries/useCtxDimensions'
import { Dimension } from '../../../../features/dimension/types/Dimension'
import DimensionToolbar from './components/DimensionToolbar'
import DimensionEditModeBar from './components/DimensionEditModeBar'
import DimensionActionBar from './components/DimensionActionBar'
import DimensionGrid, { DimensionGridMode, DimensionSourceFilter } from './components/DimensionGrid'
import DimensionInspectorPanel from './components/DimensionInspectorPanel'
import DimensionFormModal, { DimensionFormMode } from './components/DimensionFormModal'
import DimensionMoveModal from './components/DimensionMoveModal'
import DimensionDeleteConfirm from './components/DimensionDeleteConfirm'
import { useDimensionGridActions } from './hooks/useDimensionGridActions'

interface FormModalState {
  open: boolean
  mode: DimensionFormMode
  dimension?: Dimension
  parentDimensionId?: string
}

interface SelectionModalState {
  open: boolean
  dimensionIds: string[]
}

const DimensionSettingsPage: React.FC = () => {
  const isAdmin = useSelector(currentUserIsAdminSelector)
  const { data: flatList = [], isLoading, isFetching } = useCtxDimensions()

  const [editMode, setEditMode] = useState(false)
  const [viewMode, setViewMode] = useState<DimensionGridMode>('tree')
  const [searchText, setSearchText] = useState('')
  const [sourceFilter, setSourceFilter] = useState<DimensionSourceFilter>('all')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [clearSelectionSignal, setClearSelectionSignal] = useState(0)
  const [activeDimensionId, setActiveDimensionId] = useState<string | null>(null)
  const [formModal, setFormModal] = useState<FormModalState>({ open: false, mode: 'create' })
  const [moveModal, setMoveModal] = useState<SelectionModalState>({ open: false, dimensionIds: [] })
  const [deleteModal, setDeleteModal] = useState<SelectionModalState>({ open: false, dimensionIds: [] })
  const { reorderBlock, moveToEdge } = useDimensionGridActions()

  const canEdit = isAdmin && editMode

  const activeDimension = useMemo(
    () => flatList.find(d => d.dimensionId === activeDimensionId) || null,
    [flatList, activeDimensionId]
  )

  const clearSelection = () => {
    setSelectedIds([])
    setClearSelectionSignal(n => n + 1)
  }

  const closeFormModal = () => setFormModal({ open: false, mode: 'create' })
  const closeMoveModal = () => setMoveModal({ open: false, dimensionIds: [] })
  const closeDeleteModal = () => setDeleteModal({ open: false, dimensionIds: [] })

  return (
    <>
      {canEdit && <DimensionEditModeBar onDone={() => setEditMode(false)} />}

      <DimensionToolbar
        isAdmin={isAdmin}
        onToggleEditMode={() => setEditMode(v => !v)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        sourceFilter={sourceFilter}
        onSourceFilterChange={setSourceFilter}
        canEdit={canEdit}
        onAdd={() => setFormModal({ open: true, mode: 'create' })}
      />

      {canEdit && selectedIds.length > 0 && (
        <DimensionActionBar
          flatList={flatList}
          selectedIds={selectedIds}
          onMoveBlock={direction => reorderBlock(flatList, selectedIds, direction)}
          onMoveToEdge={edge => moveToEdge(flatList, selectedIds, edge)}
          onMoveUnder={() => setMoveModal({ open: true, dimensionIds: selectedIds })}
          onDelete={() => setDeleteModal({ open: true, dimensionIds: selectedIds })}
          onClear={clearSelection}
        />
      )}

      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <DimensionGrid
            mode={viewMode}
            flatList={flatList}
            loading={isLoading || isFetching}
            canEdit={canEdit}
            searchText={searchText}
            sourceFilter={sourceFilter}
            selectedIds={selectedIds}
            clearSelectionSignal={clearSelectionSignal}
            onSelectionChange={setSelectedIds}
            onActiveDimensionChange={setActiveDimensionId}
            onRequestRename={dimension => setFormModal({ open: true, mode: 'rename', dimension })}
            onRequestMoveUnder={dimensionIds => setMoveModal({ open: true, dimensionIds })}
            onRequestAddChild={parentDimensionId => setFormModal({ open: true, mode: 'addChild', parentDimensionId })}
            onRequestDelete={dimensionIds => setDeleteModal({ open: true, dimensionIds })}
          />
        </div>

        {viewMode === 'tree' && activeDimension && (
          <DimensionInspectorPanel
            dimension={activeDimension}
            flatList={flatList}
            canEdit={canEdit}
            onClose={() => setActiveDimensionId(null)}
          />
        )}
      </div>

      <DimensionFormModal
        open={formModal.open}
        mode={formModal.mode}
        dimension={formModal.dimension}
        parentDimensionId={formModal.parentDimensionId}
        flatList={flatList}
        onClose={closeFormModal}
      />
      <DimensionMoveModal
        open={moveModal.open}
        flatList={flatList}
        dimensionIds={moveModal.dimensionIds}
        onClose={() => {
          closeMoveModal()
          clearSelection()
        }}
      />
      <DimensionDeleteConfirm
        open={deleteModal.open}
        flatList={flatList}
        dimensionIds={deleteModal.dimensionIds}
        canEdit={canEdit}
        onClose={() => {
          closeDeleteModal()
          clearSelection()
        }}
      />
    </>
  )
}

export default DimensionSettingsPage
